/**
 * useSharedUrl Hook
 * 
 * Handles PWA share target functionality by detecting and processing shared URLs
 * from query parameters (?shared=true&url=...).
 * 
 * Features:
 * - Auto-detect shared URLs on app load
 * - Prevent duplicate processing via localStorage
 * - Auto-ingest shared URLs
 * - Display inline ingestion progress
 * - Clean query parameters after processing
 * 
 * Requirements: 16.5, 16.6, 16.7, 16.8
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as ingestAPI from '@/api/ingest';
import * as knowledgeAPI from '@/api/knowledge';
import { queryKeys } from '@/lib/queryKeys';
import type { IngestURLResponse } from '@/types/api';

const LAST_SHARED_URL_KEY = 'lastSharedUrl';

export interface SharedUrlState {
  isProcessing: boolean;
  progress: string | null;
  error: string | null;
}

export interface UseSharedUrlReturn {
  state: SharedUrlState;
}

/**
 * Hook to handle PWA share target URLs
 * Automatically processes shared URLs on mount and cleans up query parameters
 */
export function useSharedUrl(): UseSharedUrlReturn {
  const queryClient = useQueryClient();
  const [processingItemId, setProcessingItemId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  // URL ingestion mutation
  const urlMutation = useMutation({
    mutationFn: (url: string) => ingestAPI.url(url),
    onSuccess: (data: IngestURLResponse) => {
      setError(null);
      
      // If already ready, invalidate queries and clean up
      if (data.status === 'ready') {
        queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
        cleanQueryParams();
      } else {
        // Start polling for status
        setProcessingItemId(data.item_id);
      }
    },
    onError: (err: any) => {
      console.error('Shared URL ingestion error:', err);
      
      // Handle specific error codes
      const status = err.response?.status || err.status;
      const message = err.response?.data?.message || err.message;
      
      if (status === 409) {
        setError('This URL has already been ingested.');
      } else if (status === 422) {
        setError('Unable to access this content. It may be private or blocked.');
      } else if (status === 413) {
        setError('File size exceeds the maximum limit.');
      } else {
        setError(message || 'Failed to ingest URL. Please try again.');
      }
      
      // Clean query params even on error
      cleanQueryParams();
    },
  });

  // Poll status endpoint every 5 seconds during processing
  const { data: statusData } = useQuery({
    queryKey: ['itemStatus', processingItemId],
    queryFn: () => knowledgeAPI.getStatus(processingItemId!),
    enabled: !!processingItemId,
    refetchInterval: (query) => {
      // Stop polling when status is 'ready' or 'failed'
      if (query.state.data?.status === 'ready' || query.state.data?.status === 'failed') {
        return false;
      }
      return 5000; // Poll every 5 seconds
    },
  });

  // Handle status completion
  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === 'ready') {
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.all });
      setProcessingItemId(null);
      cleanQueryParams();
    } else if (statusData.status === 'failed') {
      setError(statusData.error || 'Failed to process URL');
      setProcessingItemId(null);
      cleanQueryParams();
    }
  }, [statusData, queryClient]);

  // Clean query parameters from URL
  const cleanQueryParams = () => {
    // Get current location without query params
    const currentPath = window.location.hash.split('?')[0];
    
    // Update URL without query params
    window.history.replaceState(null, '', currentPath);
  };

  // Check for shared URL on mount
  useEffect(() => {
    // Only process once
    if (hasProcessed) return;

    // Parse query parameters from hash
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    
    if (queryStart === -1) return;

    const queryString = hash.substring(queryStart + 1);
    const params = new URLSearchParams(queryString);
    
    const isShared = params.get('shared') === 'true';
    const sharedUrl = params.get('url');

    if (!isShared || !sharedUrl) return;

    // Check if this URL was already processed
    const lastSharedUrl = localStorage.getItem(LAST_SHARED_URL_KEY);
    
    if (lastSharedUrl === sharedUrl) {
      console.log('Shared URL already processed, skipping:', sharedUrl);
      cleanQueryParams();
      return;
    }

    // Mark as processed to prevent re-processing on re-renders
    setHasProcessed(true);

    // Store this URL as the last handled one
    localStorage.setItem(LAST_SHARED_URL_KEY, sharedUrl);

    // Ingest the URL
    console.log('Processing shared URL:', sharedUrl);
    urlMutation.mutate(sharedUrl);
  }, [hasProcessed, urlMutation]);

  // Build state object
  const state: SharedUrlState = {
    isProcessing: urlMutation.isPending || !!processingItemId,
    progress: statusData?.progress_message || null,
    error,
  };

  return {
    state,
  };
}
